/**
 * @description Base Button implementation
 * @param Widget - widget for extending
 * @returns Button widget
 */
function CoreButtonClassCreator(Widget: import('widgets/global/Button').TButton) {
    /**
     * @category widgets
     * @subcategory global
     * @class Button
     * @augments Widget
     * @classdesc Button widget, which could be used in different contexts (html-markup, inside widgets etc).
     * <br>Could be also used to trigger parent's widget method
     * @property {string} data-widget - Widget name `button`
     * @property {string} data-widget-event-click - Event listener to call Parent's widget method
     * @property {string} data-event-click - Event listener method `handleClick` for click event on widget
     * @property {boolean} data-prevent-action - Prevent action flag
     * @classdesc Represents Button component with the next features:
     * 1. Activate\deactivate button visibility on the storefront
     * 2. Enable\disable button visibility on the storefront
     * 3. Handles keydown by enter\space keyboard button
     * 4. Sets button error
     * 5. Marks button as pressed\unpressed
     *
     * @example <caption>Example of Button widget usage</caption>
     * <button
     *     type="submit"
     *     class="btn btn-block btn-primary"
     *     data-widget="button"
     *     data-widget-event-click="handleSubmit"
     *     data-event-click.prevent="handleClick"
     *     data-id="submitButton"
     * >
     *     ${Resource.msg('button.text.loginform', 'login', null)}
     * </button>
     */
    class CoreButton extends Widget {
        /**
         * @description Returns Widget configuration object
         * @returns Widget configuration object
         */
        prefs() {
            return {
                analyticsClick: null,
                ...super.prefs()
            };
        }

        /**
         * @description Handles button click
         * @listens dom:click
         * @emits Button#click
         */
        handleClick() {
            super.handleClick();

            if (!this.prefs().preventAction && this.prefs().analyticsClick) {
                this.eventBus().emit('gtm.event', this.prefs().analyticsClick);
            }
        }
    }

    return CoreButton;
}

export type TCoreButton = ReturnType<typeof CoreButtonClassCreator>;

export type TCoreButtonInstance = InstanceType<TCoreButton>;

export default CoreButtonClassCreator;
